import React, { useState, useEffect } from 'react';
import { theme, alpha } from '../../../theme';
import { X, User, Phone, Clock, Calendar, DollarSign, FileText, MessageCircle } from 'lucide-react';
import { CreateManualBookingRequest, GridVenue, BookingChannel, CHANNEL_LABELS, CHANNEL_COLORS } from '../../../types/venue';

interface CreateManualBookingModalProps {
  isOpen: boolean;
  onClose: () => void;
  onSubmit: (data: CreateManualBookingRequest) => Promise<{ success: boolean; error?: string }>;
  venues: GridVenue[];
  initialVenueId?: string;
  initialDate?: string;
  initialTime?: string;
  initialCustomerName?: string;
  initialCustomerPhone?: string;
  initialNotes?: string;
  initialBookingChannel?: BookingChannel;
}

const CreateManualBookingModal: React.FC<CreateManualBookingModalProps> = ({
  isOpen,
  onClose,
  onSubmit,
  venues,
  initialVenueId,
  initialDate,
  initialTime,
  initialCustomerName,
  initialCustomerPhone,
  initialNotes,
  initialBookingChannel,
}) => {
  const [formData, setFormData] = useState<CreateManualBookingRequest>({
    venueId: initialVenueId || '',
    bookingDate: initialDate || new Date().toISOString().split('T')[0],
    startTime: initialTime || '10:00',
    endTime: '11:00',
    customerName: '',
    customerPhone: '',
    totalAmount: undefined,
    depositPaid: undefined,
    notes: '',
    bookingChannel: initialBookingChannel || 'WHATSAPP',
  });

  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  // Update form when initial values change
  useEffect(() => {
    if (initialVenueId) setFormData((prev) => ({ ...prev, venueId: initialVenueId }));
    if (initialDate) setFormData((prev) => ({ ...prev, bookingDate: initialDate }));
    if (initialTime) {
      const [hours, minutes] = initialTime.split(':').map(Number);
      const endHours = hours + 1;
      setFormData((prev) => ({
        ...prev,
        startTime: initialTime,
        endTime: `${endHours.toString().padStart(2, '0')}:${minutes.toString().padStart(2, '0')}`,
      }));
    }
    if (initialCustomerName) setFormData((prev) => ({ ...prev, customerName: initialCustomerName }));
    if (initialCustomerPhone) setFormData((prev) => ({ ...prev, customerPhone: initialCustomerPhone }));
    if (initialNotes) setFormData((prev) => ({ ...prev, notes: initialNotes }));
    if (initialBookingChannel) setFormData((prev) => ({ ...prev, bookingChannel: initialBookingChannel }));
  }, [initialVenueId, initialDate, initialTime, initialCustomerName, initialCustomerPhone, initialNotes, initialBookingChannel]);

  // Calculate suggested price based on venue and duration
  useEffect(() => {
    if (formData.venueId && formData.startTime && formData.endTime) {
      const venue = venues.find((v) => v.id === formData.venueId);
      if (venue) {
        const [startH, startM] = formData.startTime.split(':').map(Number);
        const [endH, endM] = formData.endTime.split(':').map(Number);
        const durationHours = (endH * 60 + endM - startH * 60 - startM) / 60;
        if (durationHours > 0) {
          const suggestedPrice = durationHours * venue.hourlyRate;
          setFormData((prev) => ({
            ...prev,
            totalAmount: prev.totalAmount === undefined ? suggestedPrice : prev.totalAmount,
          }));
        }
      }
    }
  }, [formData.venueId, formData.startTime, formData.endTime, venues]);

  const venueDepositAmount = formData.venueId
    ? venues.find((v) => v.id === formData.venueId)?.depositAmount
    : undefined;

  if (!isOpen) return null;

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError(null);

    if (!formData.customerName.trim()) {
      setError('Ingrese el nombre del cliente');
      return;
    }

    setLoading(true);
    const result = await onSubmit(formData);
    setLoading(false);

    if (result.success) {
      onClose();
      // Reset form
      setFormData({
        venueId: '',
        bookingDate: new Date().toISOString().split('T')[0],
        startTime: '10:00',
        endTime: '11:00',
        customerName: '',
        customerPhone: '',
        totalAmount: undefined,
        depositPaid: undefined,
        notes: '',
        bookingChannel: 'WHATSAPP',
      });
    } else {
      setError(result.error || 'Error al crear reserva');
    }
  };

  const overlayStyle: React.CSSProperties = {
    position: 'fixed',
    inset: 0,
    backgroundColor: 'rgba(0, 0, 0, 0.7)',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    zIndex: 1000,
    padding: '20px',
  };

  const modalStyle: React.CSSProperties = {
    backgroundColor: theme.colors.surface,
    borderRadius: theme.borderRadius.card,
    width: '100%',
    maxWidth: '500px',
    maxHeight: '90vh',
    overflow: 'auto',
    border: `1px solid ${theme.colors.border}`,
  };

  const headerStyle: React.CSSProperties = {
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'center',
    padding: '20px 24px',
    borderBottom: `1px solid ${theme.colors.border}`,
  };

  const titleStyle: React.CSSProperties = {
    fontSize: theme.fonts.sizeSubtitle,
    fontWeight: theme.fonts.weightBold,
    color: theme.colors.text,
  };

  const closeButtonStyle: React.CSSProperties = {
    background: 'none',
    border: 'none',
    cursor: 'pointer',
    padding: '8px',
    borderRadius: '50%',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
  };

  const formStyle: React.CSSProperties = {
    padding: '24px',
  };

  const fieldStyle: React.CSSProperties = {
    marginBottom: '20px',
  };

  const labelStyle: React.CSSProperties = {
    display: 'flex',
    alignItems: 'center',
    gap: '8px',
    fontSize: theme.fonts.sizeSmall,
    fontWeight: theme.fonts.weightBold,
    color: theme.colors.textSecondary,
    marginBottom: '8px',
  };

  const inputStyle: React.CSSProperties = {
    width: '100%',
    padding: '12px 16px',
    borderRadius: theme.borderRadius.input,
    border: `1px solid ${theme.colors.border}`,
    backgroundColor: theme.colors.background,
    color: theme.colors.text,
    fontSize: theme.fonts.sizeBody,
    outline: 'none',
  };

  const selectStyle: React.CSSProperties = {
    ...inputStyle,
    cursor: 'pointer',
  };

  const rowStyle: React.CSSProperties = {
    display: 'flex',
    gap: '16px',
  };

  const halfFieldStyle: React.CSSProperties = {
    flex: 1,
  };

  const errorStyle: React.CSSProperties = {
    backgroundColor: `${alpha('danger', '20')}`,
    color: theme.colors.danger,
    padding: '12px 16px',
    borderRadius: theme.borderRadius.input,
    fontSize: theme.fonts.sizeSmall,
    marginBottom: '16px',
  };

  const footerStyle: React.CSSProperties = {
    display: 'flex',
    justifyContent: 'flex-end',
    gap: '12px',
    padding: '16px 24px',
    borderTop: `1px solid ${theme.colors.border}`,
  };

  const buttonStyle: React.CSSProperties = {
    padding: '12px 24px',
    borderRadius: theme.borderRadius.button,
    fontSize: theme.fonts.sizeSmall,
    fontWeight: theme.fonts.weightBold,
    cursor: 'pointer',
    transition: 'all 0.2s',
  };

  const cancelButtonStyle: React.CSSProperties = {
    ...buttonStyle,
    backgroundColor: 'transparent',
    border: `1px solid ${theme.colors.border}`,
    color: theme.colors.text,
  };

  const submitButtonStyle: React.CSSProperties = {
    ...buttonStyle,
    backgroundColor: theme.colors.primary,
    border: 'none',
    color: theme.colors.background,
  };

  return (
    <div data-testid="booking-modal" style={overlayStyle} onClick={onClose}>
      <div style={modalStyle} onClick={(e) => e.stopPropagation()}>
        <div style={headerStyle}>
          <h2 style={titleStyle}>Nueva Reserva Manual</h2>
          <button style={closeButtonStyle} onClick={onClose}>
            <X size={24} color={theme.colors.textSecondary} />
          </button>
        </div>

        <form onSubmit={handleSubmit} style={formStyle}>
          {error && <div style={errorStyle}>{error}</div>}

          <div style={fieldStyle}>
            <label style={labelStyle}>
              <Calendar size={16} />
              Cancha
            </label>
            <select
              style={selectStyle}
              value={formData.venueId}
              onChange={(e) => setFormData({ ...formData, venueId: e.target.value })}
              required
            >
              <option value="">Seleccionar cancha</option>
              {venues.map((v) => (
                <option key={v.id} value={v.id}>
                  {v.name} ({v.venueType.replace('FUTBOL_', 'F')})
                </option>
              ))}
            </select>
          </div>

          <div style={fieldStyle}>
            <label style={labelStyle}>
              <Calendar size={16} />
              Fecha
            </label>
            <input
              type="date"
              style={{ ...inputStyle, colorScheme: 'dark' }}
              value={formData.bookingDate}
              onChange={(e) => setFormData({ ...formData, bookingDate: e.target.value })}
              required
            />
          </div>

          <div style={rowStyle}>
            <div style={halfFieldStyle}>
              <label style={labelStyle}>
                <Clock size={16} />
                Hora Inicio
              </label>
              <input
                type="time"
                style={{ ...inputStyle, colorScheme: 'dark' }}
                value={formData.startTime}
                onChange={(e) => setFormData({ ...formData, startTime: e.target.value })}
                required
              />
            </div>
            <div style={halfFieldStyle}>
              <label style={labelStyle}>
                <Clock size={16} />
                Hora Fin
              </label>
              <input
                type="time"
                style={{ ...inputStyle, colorScheme: 'dark' }}
                value={formData.endTime}
                onChange={(e) => setFormData({ ...formData, endTime: e.target.value })}
                required
              />
            </div>
          </div>

          <div style={fieldStyle}>
            <label style={labelStyle}>
              <User size={16} />
              Nombre del Cliente *
            </label>
            <input
              data-testid="booking-customer-name"
              type="text"
              style={inputStyle}
              value={formData.customerName}
              onChange={(e) => setFormData({ ...formData, customerName: e.target.value })}
              placeholder="Nombre completo"
              required
            />
          </div>

          <div style={fieldStyle}>
            <label style={labelStyle}>
              <Phone size={16} />
              Teléfono (opcional)
            </label>
            <input
              type="tel"
              style={inputStyle}
              value={formData.customerPhone || ''}
              onChange={(e) => setFormData({ ...formData, customerPhone: e.target.value })}
              placeholder="+54 11 1234-5678"
            />
          </div>

          <div style={fieldStyle}>
            <label style={labelStyle}>
              <MessageCircle size={16} />
              Canal de contacto
            </label>
            <div style={{ display: 'flex', gap: '8px', flexWrap: 'wrap' }}>
              {(Object.keys(CHANNEL_LABELS) as BookingChannel[]).filter(ch => ch !== 'WEB').map((channel) => (
                <button
                  key={channel}
                  type="button"
                  onClick={() => setFormData({ ...formData, bookingChannel: channel })}
                  style={{
                    padding: '8px 14px',
                    borderRadius: theme.borderRadius.button,
                    fontSize: '13px',
                    fontWeight: formData.bookingChannel === channel ? theme.fonts.weightBold : 'normal',
                    cursor: 'pointer',
                    border: formData.bookingChannel === channel
                      ? `2px solid ${CHANNEL_COLORS[channel]}`
                      : `1px solid ${theme.colors.border}`,
                    backgroundColor: formData.bookingChannel === channel
                      ? `${CHANNEL_COLORS[channel]}20`
                      : 'transparent',
                    color: formData.bookingChannel === channel
                      ? CHANNEL_COLORS[channel]
                      : theme.colors.textSecondary,
                    transition: 'all 0.15s',
                  }}
                >
                  {CHANNEL_LABELS[channel]}
                </button>
              ))}
            </div>
          </div>

          <div style={fieldStyle}>
            <label style={labelStyle}>
              <DollarSign size={16} />
              Monto Total (opcional)
            </label>
            <input
              type="number"
              style={inputStyle}
              value={formData.totalAmount || ''}
              onChange={(e) =>
                setFormData({
                  ...formData,
                  totalAmount: e.target.value ? Number(e.target.value) : undefined,
                })
              }
              placeholder="Se calculará automáticamente si no se especifica"
              step="0.01"
              min="0"
            />
          </div>

          {venueDepositAmount != null && venueDepositAmount > 0 && (
            <div style={fieldStyle}>
              <label style={labelStyle}>
                <DollarSign size={16} />
                Sena (opcional)
              </label>
              <div style={{ display: 'flex', gap: '8px' }}>
                <input
                  type="number"
                  style={{ ...inputStyle, flex: 1 }}
                  value={formData.depositPaid ?? ''}
                  onChange={(e) =>
                    setFormData({
                      ...formData,
                      depositPaid: e.target.value ? Number(e.target.value) : undefined,
                    })
                  }
                  placeholder="Monto de sena"
                  step="0.01"
                  min="0"
                />
                <button
                  type="button"
                  style={{
                    padding: '12px 16px',
                    borderRadius: theme.borderRadius.button,
                    fontSize: theme.fonts.sizeSmall,
                    fontWeight: theme.fonts.weightBold,
                    cursor: 'pointer',
                    border: `1px solid ${theme.colors.primary}`,
                    backgroundColor: formData.depositPaid === venueDepositAmount ? `${alpha('primary', '30')}` : 'transparent',
                    color: theme.colors.primary,
                    whiteSpace: 'nowrap',
                  }}
                  onClick={() =>
                    setFormData({ ...formData, depositPaid: venueDepositAmount })
                  }
                >
                  Marcar pagada (${venueDepositAmount})
                </button>
              </div>
            </div>
          )}

          <div style={fieldStyle}>
            <label style={labelStyle}>
              <FileText size={16} />
              Notas (opcional)
            </label>
            <textarea
              style={{ ...inputStyle, minHeight: '80px', resize: 'vertical' }}
              value={formData.notes || ''}
              onChange={(e) => setFormData({ ...formData, notes: e.target.value })}
              placeholder="Observaciones adicionales..."
            />
          </div>

          <div style={footerStyle}>
            <button type="button" style={cancelButtonStyle} onClick={onClose}>
              Cancelar
            </button>
            <button
              data-testid="booking-submit-btn"
              type="submit"
              style={submitButtonStyle}
              disabled={loading}
            >
              {loading ? 'Creando...' : 'Crear Reserva'}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
};

export default CreateManualBookingModal;
